import React, { useEffect, useRef, useState } from "react";

import moment from "moment"

import {
    List, ListItem, ListItemText, ListItemIcon,
    Typography, Tooltip, Link, Avatar, Chip, Grid, Checkbox, LinearProgress, Box, CircularProgress, Fade
} from "@material-ui/core";
import { withStyles, makeStyles } from "@material-ui/core/styles";

import { requestServices } from "../../../domain/constant/request.services.constant";

import {
    AttachFileOutlined, Close, GetApp, Visibility,
    Edit, LockOpenOutlined, LockOutlined, FileCopyOutlined, OutlinedFlag
} from "@material-ui/icons";
import { Check } from "react-feather";

import documentsService from "../../../domain/services/solicitation/documents.service";
import loginService from "../../../domain/services/login.service";
import { action } from '../../../domain/constant/documents.history.constant';
import documentVerification from '../../../domain/services/solicitation/document.verification';
import ExpiredDocumentBadge from '../../shared/ExpiredDocumentBadge';
import { isExpiredDocument } from '../../../domain/services/solicitation/document.expiration';
import UploadRow from './upload.row.component';
import { isUploadRow } from '../../../domain/services/upload/upload.rows';

const PENDING_ACCENT_COLOR = '#376fd0';
const PENDING_ACCENT_BG = 'rgba(55,111,208,0.16)';
const RAF_THROTTLE_MS = 100;

const PendingProgressBar = withStyles({
    colorPrimary: { backgroundColor: PENDING_ACCENT_BG },
    barColorPrimary: {
        backgroundColor: PENDING_ACCENT_COLOR,
        transition: 'transform 200ms linear',
    },
})(LinearProgress);

const ANALYZE_RANGE_START = 30;
const ANALYZE_RANGE_END = 95;
const ANALYZE_ESTIMATED_MS = 8000;

const computeDocRatio = (doc) => {
    if (doc.status === 'finalized') return 1.0;
    if (doc.status === 'awaiting') return ANALYZE_RANGE_END / 100;
    if (doc.status === 'uploading') {
        if (typeof doc.uploadProgress !== 'number') return 0;
        return (doc.uploadProgress / 100) * (ANALYZE_RANGE_START / 100);
    }
    if (doc.status === 'analyzing') {
        const startedAt = doc.analyzingStartedAt || Date.now();
        const elapsed = Date.now() - startedAt;
        const linear = Math.min(elapsed / ANALYZE_ESTIMATED_MS, 0.99);
        const eased = 1 - Math.pow(1 - linear, 2);
        return (ANALYZE_RANGE_START + eased * (ANALYZE_RANGE_END - ANALYZE_RANGE_START)) / 100;
    }
    return 0;
};

const PendingBanner = ({ pendingDocs, paused }) => {
    const [, setTick] = useState(0);
    const lastDisplayRef = useRef(0);

    const docs = pendingDocs || [];
    const count = docs.length;
    const needsTick = !paused && docs.some(d =>
        d.status === 'analyzing' ||
        (d.status === 'uploading' && typeof d.uploadProgress !== 'number')
    );

    useEffect(() => {
        if (!needsTick) return undefined;
        let rafId;
        let lastSetAt = performance.now();
        const loop = () => {
            const now = performance.now();
            if (now - lastSetAt >= RAF_THROTTLE_MS) {
                lastSetAt = now;
                setTick(t => t + 1);
            }
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(rafId);
    }, [needsTick]);

    useEffect(() => {
        if (count === 0) lastDisplayRef.current = 0;
    }, [count]);

    if (count === 0) return null;

    const computed = docs.reduce((sum, d) => sum + computeDocRatio(d), 0) / count;
    const value = Math.max(lastDisplayRef.current, computed) * 100;
    lastDisplayRef.current = value / 100;

    return (
        <Box style={{ marginBottom: 8, padding: '4px 0', opacity: paused ? 0.35 : 1, transition: 'opacity 200ms ease' }}>
            <PendingProgressBar variant="determinate" value={value} style={{ height: 2, borderRadius: 1 }} />
        </Box>
    );
};

const pluralizeDocs = (verb, count) =>
    count === 1 ? `${verb} documento…` : `${verb} ${count} documentos…`;

const resolveHeaderCaption = (pendingDocs, paused, isEdit) => {
    const count = (pendingDocs || []).length;
    if (count === 0) {
        return isEdit
            ? 'Selecione os documentos para Ativar/Cancelar a opção de Download'
            : null;
    }
    const hasAwaiting = paused || pendingDocs.some(d => d.status === 'awaiting');
    const hasUploading = pendingDocs.some(d => d.status === 'uploading');
    if (hasAwaiting) return 'Aguardando resolução dos documentos pendentes…';
    if (hasUploading) return pluralizeDocs('Enviando', count);
    return pluralizeDocs('Analisando', count);
};

const usePendingStyles = makeStyles({
    '@keyframes pendingFadeIn': {
        from: { opacity: 0, transform: 'translateY(8px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    row: {
        animation: '$pendingFadeIn 240ms cubic-bezier(0.25, 0.1, 0.25, 1) both',
    },
});

// Ação que só aparece quando o mouse está sobre a linha do documento
// (ex.: "Definir como documento do processo"), para não poluir a lista.
const useRowActionStyles = makeStyles({
    row: {
        '&:hover $hoverAction, &:focus-within $hoverAction': { opacity: 1 },
    },
    hoverAction: {
        opacity: 0,
        transition: 'opacity 120ms ease',
    },
});

const DOT_INTERVAL_MS = 500;
const useAnimatedDots = (paused = false) => {
    const [, setTick] = useState(0);
    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(() => setTick(t => t + 1), DOT_INTERVAL_MS);
        return () => clearInterval(id);
    }, [paused]);
    if (paused) return '';
    return '.'.repeat(Math.floor(Date.now() / DOT_INTERVAL_MS) % 4);
};

const ANALYZING_SUB_STATES = ['Analisando conteúdo…', 'Identificando o tipo do documento…', 'Conferindo aderência ao caso…'];
const ANALYZING_ROTATION_MS = 1500;

const PendingRow = ({ doc, index = 0, className, paused = false }) => {
    const isAwaiting = paused || doc.status === 'awaiting';
    const dots = useAnimatedDots(isAwaiting && doc.status !== 'analyzing');

    const [analyzingSubIdx, setAnalyzingSubIdx] = useState(0);
    useEffect(() => {
        if (doc.status !== 'analyzing') {
            setAnalyzingSubIdx(0);
            return undefined;
        }
        if (analyzingSubIdx >= ANALYZING_SUB_STATES.length - 1) return undefined;
        const t = setTimeout(() => setAnalyzingSubIdx(prev => Math.min(prev + 1, ANALYZING_SUB_STATES.length - 1)), ANALYZING_ROTATION_MS);
        return () => clearTimeout(t);
    }, [doc.status, analyzingSubIdx]);

    const secondaryText = (() => {
        if (isAwaiting) return 'Aguardando';
        if (doc.status === 'uploading') {
            const progress = typeof doc.uploadProgress === 'number' ? doc.uploadProgress : 0;
            return progress < 100 ? `Carregando arquivo${dots}` : `Subindo para o servidor${dots}`;
        }
        if (doc.status === 'analyzing') return ANALYZING_SUB_STATES[analyzingSubIdx];
        return `Carregando${dots}`;
    })();
    return (
        <ListItem
            className={className}
            style={{
                paddingLeft: 0,
                width: '100%',
                background: 'white',
                borderBottom: 'solid 1px #ededed',
                animationDelay: `${index * 80}ms`,
            }}
        >
            <ListItemIcon>
                <Avatar
                    alt={doc.name}
                    style={{
                        background: '#009700',
                        color: 'white', width: 24, height: 24,
                        fontSize: 10, fontWeight: 600, marginLeft: 15,
                    }}
                >
                    <AttachFileOutlined style={{ fontSize: '16px' }} />
                </Avatar>
            </ListItemIcon>
            <ListItemText
                style={{ flex: '5 1 auto', marginRight: 10 }}
                size="small"
                primary={doc.name}
                secondary={secondaryText}
                primaryTypographyProps={{
                    style: {
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                }}
            />
            <CircularProgress
                size={16}
                thickness={4}
                variant={isAwaiting ? 'determinate' : 'indeterminate'}
                value={isAwaiting ? 80 : undefined}
                style={{
                    color: PENDING_ACCENT_COLOR,
                    marginRight: 16,
                    opacity: isAwaiting ? 0.35 : 1,
                    transition: 'opacity 200ms ease',
                }}
            />
        </ListItem>
    );
};

const RequestDocumentList = (
    { request, documents = [], selectFile, visibility,
        handleOpenConfig, removeDocument = () => { },
        update, isEdit, isProcessDocumentSelection,
        setProcessMainDocument = () => { }, unsetProcessMainDocument = () => { },
        pauseAllPending = false, onRemovePending = () => { },
    }
) => {

    const pendingClasses = usePendingStyles();
    const rowActionClasses = useRowActionStyles();

    const checkColorFile = (item) => {
        return documentVerification.statusColor(item.verificationInfo) || "#009700";
    }

    const checkColorRow = (item) => item.checked ? "#f0f8ffdb" : "white";

    const getTag = (key, item) => {

        const tagInfo = documentsService.tagInfoByKey(key, item, request);

        return (
            <Tooltip title={tagInfo.message}>
                <Grid item style={{ marginRight: 8 }}>
                    <Chip
                        label={tagInfo.title} size="small"
                        style={{
                            background: tagInfo.color, color: '#FFF',
                            height: 18, fontSize: 10, fontWeight: 600,
                        }}
                    />
                </Grid>
            </Tooltip>
        )
    }

    const getVersionTagKey = (item) => {
        if (item.isUnderCorrection && item.isCurrent) return 'underCorrection';
        if (item.isCurrent === true && item.correctionVersion > 0) return 'versionCurrent';
        if (item.isCurrent === false && item.correctionVersion >= 0) return 'versionPrevious';
        return null;
    };

    const getInternalTags = (item) => {

        if (loginService.isCustomer())
            return;

        const versionTagKey = getVersionTagKey(item);

        return (
            <>

                {versionTagKey && getTag(versionTagKey, item)}

                {!versionTagKey && item.forCorrection && getTag('correction', item)}

                {item.isProcessMainDocument && getTag('isProcessMainDocument', item)}

                {item.isServiceMainDocument && getTag('serviceMainDocument', item)}

                {item.isVisibleCustomer && !versionTagKey && (
                    item.serviceId ? getTag('finalVersion', item) : getTag('isVisibleCustomer', item)
                )}

                {loginService.isInternal() && item.isIaPetitionReview && getTag('isIAPetitionReview', item)}

                {!loginService.isCustomer() && (item.wasDevelopedWithIa && item.serviceId == requestServices.structure.petition.id) && getTag('wasDevelopedWithIa', item)}

            </>
        )
    }

    const getCustomerTags = (item) => {

        if (!loginService.isCustomer())
            return;

        const versionTagKey = getVersionTagKey(item);

        return (
            <>

                {item.isProcessMainDocument && getTag('isProcessMainDocument', item)}

                {versionTagKey && getTag(versionTagKey, item)}

                {!versionTagKey && item.forCorrection && getTag('correction', item)}

                {sourceIsNotCustomer(item.source) && getTag('internalUpload', item)}

            </>
        )
    }

    const handleChangeProcessDocument = (doc) => {
        // Só o modal de seleção do cliente usa o clique na linha inteira.
        // Na visão interna o processo é definido pelo botão dedicado (getSetProcessButton),
        // para o clique acidental na linha não trocar o documento do processo.
        if (!isProcessDocumentSelection) return;
        if (doc.isProcessMainDocument) return unsetProcessMainDocument(doc);
        return setProcessMainDocument(doc);
    }

    // Entrega do serviço (peça, coletânea etc.) nunca pode virar "documento do processo".
    const isServiceDeliverable = (doc) => !!doc.serviceId || !!doc.isServiceMainDocument;

    const canSetProcessDocument = (doc) =>
        !isProcessDocumentSelection
        && !loginService.isCustomer()
        && !doc.isProcessMainDocument
        && !doc.correctionSource
        && !isServiceDeliverable(doc);

    // Botão "Definir como documento do processo" — só no hover da linha (rowActionClasses.hoverAction),
    // só para usuário interno e apenas em documentos de entrada. A troca De→Para e a confirmação
    // vivem no componente pai (setProcessMainDocument), que enxerga a lista completa.
    const getSetProcessButton = (doc) => {
        if (!canSetProcessDocument(doc)) return null;

        return (
            <Tooltip title="Definir como documento do processo">
                <Link
                    className={rowActionClasses.hoverAction}
                    style={{ marginLeft: 8, color: '#376fd0', cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setProcessMainDocument(doc);
                    }}
                >
                    <OutlinedFlag style={{ fontSize: 16 }} />
                </Link>
            </Tooltip>
        );
    }

    const getItemDocument = (item) => {

        const senderUserName = (!loginService.isCustomer() || !sourceIsNotCustomer(item.source)) ? `${(item.senderUserName || "Facilita Jurídico")} - ` : ""

        const documentValue = {
            name: item.displayName || item.name,
            created: (item.created || moment()),
            senderUserName,
            error: documentVerification.listSuffix(item.verificationInfo)
        }

        const dateTime = convertDateTimeToLocaleString(documentValue.created);

        return (
            <ListItemText style={{ flex: "5 1 auto", marginRight: 10 }} size="small"
                primary={
                    <Grid>
                        <Grid container alignItems="center">

                            <Grid style={{ marginRight: 8 }}>
                                {documentValue.name} {documentValue.error}
                            </Grid>

                            {getInternalTags(item)}

                            {getCustomerTags(item)}

                        </Grid>

                        {getNameTypeChip(item)}
                    </Grid>
                }
                secondary={`${documentValue.senderUserName}${dateTime}`}
            />
        );
    }

    const getNameTypeChip = (item) => {

        if (!item.nameType) return null

        const title = documentsService
            .uploadListByAreaId(request.areaId)
            .find(ul => ul.nameType == item.nameType)?.title

        return (
            <Chip
                avatar={<Check />} size="small"
                style={{
                    background: '#B9F8D3', fontStyle: 'italic',
                    color: '#184D47', margin: '5px 0', height: '20px'
                }}
                label={
                    <Tooltip title={title || ""}>
                        <Typography style={{
                            overflow: "hidden", textOverflow: "ellipsis",
                            maxWidth: '225px', fontSize: "10px"
                        }}>
                            {title}
                        </Typography>
                    </Tooltip>
                }
            />
        )
    }

    const convertDateTimeToLocaleString = (date) => {

        let convertDate = date ? new Date(date) : "";

        if (convertDate)
            convertDate = `${convertDate.toLocaleDateString('pt-BR')} - ${convertDate.toLocaleTimeString('pt-BR')} `;

        return convertDate;
    }

    const ActionButton = ({ description, color, icon: Icon, onClick }) => (
        <Tooltip title={description}>
            <Link style={{ marginLeft: 8, color }} onClick={onClick}>
                <Icon style={{ fontSize: 16 }} />
            </Link>
        </Tooltip>
    )

    const getVisibilityButton = (doc) => {

        if (!visibility || isExpiredDocument(doc))
            return null;

        return (
            <ActionButton
                description="Visualizar documento" icon={Visibility}
                onClick={() => viewFile(doc)}
            />
        )
    }

    const getDownloadAction = (requestDocument) => isExpiredDocument(requestDocument)
        ? <ExpiredDocumentBadge />
        : <ActionButton description="Baixar documento" icon={GetApp} onClick={() => downloadFile(requestDocument)} />;

    const downloadFile = (doc) => {
        documentsService.downloadFile(doc.url, doc.downloadName || doc.name)

        if (loginService.isFaciliter())
            registerDocumentHistory(doc, action.DOWNLOAD_DOCUMENT)
    }

    const viewFile = async (doc) => {
        if (loginService.isFaciliter())
            await registerDocumentHistory(doc, action.VIEW_DOCUMENT)

        visibility(doc, true)
    }

    const registerDocumentHistory = async (doc, action) => {
        const documentHistory = {
            documentId: doc.id,
            action: action.action,
            description: `O faciliter ${loginService.getUserAuthentication().name} ${action.pastAction} o arquivo ${doc.name}.`
        }

        await documentsService.createDocumentInteractionRecord(request.id, documentHistory).catch(err => null)
    }

    const getRemoveButton = (doc, i) => {

        if (doc.isProcessMainDocument && loginService.isFaciliter()) return null;

        const uploadedDoc = doc.senderUserId == loginService.getUserAuthentication().id;
        const canRemove = documentsService.isAllowEnableToCustomer(request) || uploadedDoc

        if (!canRemove || isProcessDocumentSelection)
            return null;

        return (
            <ActionButton
                description="Remover documento" icon={Close}
                onClick={() => removeDocument(doc, i)} color='red'
            />
        )
    }

    const getEditServiceConfigButton = (doc) => {

        const uploadedDoc = doc.senderUserId == loginService.getUserAuthentication().id;
        const canEditConfig = documentsService.isAllowEnableToCustomer(request) || uploadedDoc
        const isCorrectionDocument = !!doc.correctionSource;

        if (!canEditConfig || doc.source == 'customer' || loginService.isCustomer() || isProcessDocumentSelection || isCorrectionDocument)
            return null;

        if (request.isLegacyServiceDocument)
            return (
                <ActionButton
                    description="Editar configurações de serviço"
                    icon={doc.isVisibleCustomer ? LockOpenOutlined : LockOutlined}
                    color={!doc.isVisibleCustomer && 'gray'}
                    onClick={() => update(doc.id, { isVisibleCustomer: !doc.isVisibleCustomer })}
                />
            )

        return (
            <ActionButton
                description="Editar configurações de serviço"
                icon={Edit} onClick={() => handleOpenConfig([doc], true)}
            />
        )
    }

    const scroll = !!(isProcessDocumentSelection && documents.length > 3) || false;

    const listStyle = {
        width: "100%",
        height: "auto",
        marginBottom: 12,
        overflow: isProcessDocumentSelection ? "auto" : "visible",
        display: "block",
        maxHeight: scroll ? 200 : "none"
    };

    const sourceIsNotCustomer = (source) =>
        source != 'customer' && source != 'customerUser';

    const pendingDocs = documents.filter(d => d._isPending && !isUploadRow(d));
    const uploadRows = documents.filter(isUploadRow);

    if (documents.length)
        return (
            <section style={{ width: "100%" }}>

                {!!isEdit &&
                    <Typography variant='h6' style={{ marginBottom: 10, display: 'block' }}>
                        <strong>Documentos da Solicitação </strong>
                    </Typography>
                }

                {(() => {
                    const caption = resolveHeaderCaption(pendingDocs, pauseAllPending, isEdit);
                    if (!caption) return null;
                    return (
                        <Box style={{ minHeight: 18, position: 'relative' }}>
                            <Fade in key={caption} timeout={240}>
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ display: 'block', position: 'absolute', top: 0, left: 0, right: 0 }}
                                >
                                    {caption}
                                </Typography>
                            </Fade>
                        </Box>
                    );
                })()}

                <PendingBanner pendingDocs={pendingDocs} paused={pauseAllPending} />

                {!!uploadRows.length && (
                    <ul aria-label='Arquivos sendo enviados' className='mb-2 list-none p-0'>
                        {uploadRows.map(doc => <UploadRow key={doc._localId} doc={doc} onRemove={onRemovePending} />)}
                    </ul>
                )}

                <List
                    component="nav" dense={true}
                    style={listStyle}
                >
                    {pendingDocs.map((doc, i) => (
                        <PendingRow
                            key={doc._localId || `pending-${i}`}
                            doc={doc}
                            index={i}
                            className={pendingClasses.row}
                            paused={pauseAllPending}
                        />
                    ))}

                    {(() => {
                        const finalDocs = documents.filter(d => !d._isPending);
                        return finalDocs.map((doc, i) => (
                        !(doc.isProcessMainDocument && sourceIsNotCustomer(doc.source) && loginService.isCustomer()) && !doc.correctionSource && (
                            <ListItem
                                key={i}
                                button
                                className={rowActionClasses.row}
                                onClick={() => handleChangeProcessDocument(doc)}
                                style={{
                                    background: checkColorRow(doc),
                                    paddingLeft: 0,
                                    width: "100%",
                                    borderBottom: i < finalDocs.length - 1 ? 'solid 1px #ededed' : 'none',
                                }}
                            >

                                <section
                                    style={{ width: "100%", display: "contents" }}
                                    onClick={() => !!selectFile && selectFile(i, !doc.checked)}
                                >

                                    <ListItemIcon>
                                        <Avatar
                                            alt={doc.name}
                                            style={{
                                                background: checkColorFile(doc),
                                                color: "white", width: 24, height: 24,
                                                fontSize: 10, fontWeight: 600, marginLeft: 15
                                            }}
                                        >
                                            <AttachFileOutlined style={{ fontSize: "16px" }} />
                                        </Avatar>
                                    </ListItemIcon>

                                    {getItemDocument(doc)}


                                </section>

                                {getVisibilityButton(doc)}

                                {getSetProcessButton(doc)}

                                {!isProcessDocumentSelection && getDownloadAction(doc)}

                                {getRemoveButton(doc, i)}

                                {getEditServiceConfigButton(doc)}

                                {
                                    isProcessDocumentSelection &&
                                    <Checkbox
                                        edge="end"
                                        checked={doc.isProcessMainDocument}
                                        onChange={() => { }}
                                        color="primary"
                                    />
                                }

                            </ListItem>

                        )
                        ));
                    })()}
                </List>

            </section>
        )

    return null;
}

export default RequestDocumentList;
